import {
  PolytemporalAuditoryField,
  describePolytemporalStimulus,
  polytemporalSceneSettings,
} from "./polytemporal-audio.js";
import {
  HypnoticSoundscape,
  somaticSceneSettings,
} from "./hypnotic-soundscape.js";
import {
  AudibleRhythmCueController,
} from "./audible-rhythm-cues.js";

export function resolvePhaseSweepDepth(targetHz, scene = {}) {
  if (scene?.binauralEnabled === false) return 0;
  const numericHz = Number(targetHz) || 0;
  return numericHz > 0 ? Math.min(numericHz * 0.4, 1.8) : 0;
}

/**
 * NaN-safe scene BPM. `Number(scene.bpm || 36)` looks guarded but is not: a
 * truthy non-numeric bpm (e.g. "36 bpm", "fast") survives `|| 36`, Number()
 * yields NaN, and Math.min/Math.max PROPAGATE NaN instead of clamping it. The
 * NaN then reaches `pulseLfo.frequency.setTargetAtTime(bpm / 60, ...)` and Web
 * Audio throws:
 *   "Failed to execute 'setTargetAtTime' on 'AudioParam':
 *    The provided float value is non-finite."
 * That throw aborts the entire scene-apply pass and can silence the session.
 */
export function resolveSceneBpm(value, fallback = 36, minimum = 18, maximum = 90) {
  const numeric = Number(value);
  const base = Number.isFinite(numeric) ? numeric : fallback;
  return Math.max(minimum, Math.min(maximum, base));
}

/**
 * AudioDeckController owns the only media element in the new player.
 *
 * We deliberately keep transport simple: one visible deck, one source of truth,
 * and a tiny API surface. This removes crossfade/overlay fragility from the
 * critical playback path while we modernize the rest of the stack.
 */
export class AudioDeckController {
  constructor(audioElement) {
    this.audio = audioElement;
    this.trackEndedListeners = [];
    this.playbackListeners = [];
    this.trackErrorListeners = [];
    this.transportHandlers = null;
    this.transportPaused = false;

    this._onPlay = () => {
      this.rhythmicCue?.setPlaying(true);
      this.emitPlaybackState(true);
    };
    this._onPause = () => {
      this.rhythmicCue?.setPlaying(false);
      this.emitPlaybackState(false);
    };
    this._onEnded = () => {
      this.emitPlaybackState(false);
      // Preserve the synthesized texture/pulse across deck handoffs. Killing
      // it here turns every normal track boundary into audible dead air.
      this.trackEndedListeners.forEach((listener) => listener());
    };
    this._onError = () => {
      const mediaError = this.audio.error;
      const detail = mediaError?.message || `HTMLMediaElement error code ${mediaError?.code || "unknown"}`;
      // Media may recover or fail forward to the next track; keep the ambient
      // continuity layer running so a network error does not break immersion.
      this.trackErrorListeners.forEach((listener) => listener(detail));
    };

    this.audio.addEventListener("play", this._onPlay);
    this.audio.addEventListener("pause", this._onPause);
    this.audio.addEventListener("ended", this._onEnded);
    this.audio.addEventListener("error", this._onError);

    this.registerMediaSessionHandlers();
  }

  destroy() {
    this.pause();
    this.polytemporalField?.destroy();
    this.polytemporalField = null;
    this.hypnoticSoundscape?.destroy();
    this.hypnoticSoundscape = null;
    this.rhythmicCue?.destroy();
    this.rhythmicCue = null;
    this.audio.removeEventListener("play", this._onPlay);
    this.audio.removeEventListener("pause", this._onPause);
    this.audio.removeEventListener("ended", this._onEnded);
    this.audio.removeEventListener("error", this._onError);
    this.trackEndedListeners = [];
    this.playbackListeners = [];
    this.trackErrorListeners = [];
  }

  setupEntrainment() {
    if (this.audioContext) return;
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    this.audioContext = new AudioContext();
    // All synthesized layers receive the same AudioContext epoch. Individual
    // worklets retain their own DSP, but their slow clocks now start from one
    // auditable phase instead of drifting by however long registration took.
    this.entrainmentEpoch = this.audioContext.currentTime;

    // Director, 2026-07-24: "brainwaves are way too loud, they should be
    // masked by music" — every synthesized entrainment layer (binaural,
    // texture, pulse, isochronic) now routes through ONE shared master gain
    // instead of hitting audioContext.destination directly, so the whole
    // effects bus can be ducked as a single coherent thing rather than four
    // separately-tuned constants.
    //
    // What this does NOT do: dynamically analyze the actual music/voice
    // level. The main deck's <audio> element (this.audio) is deliberately
    // NEVER routed through createMediaElementSource — Android has
    // repeatedly turned that into a playing-but-silent graph for exactly
    // this element (see main.jsx's overlay-lane comment: "Audible speech
    // deliberately stays on the native HTMLMediaElement path"). So masking
    // here is static: it leans on the scene's own already-authored
    // musicDucking intent (light/moderate/strong, generated per-category in
    // pipeline.py) plus meaningfully lower absolute ceilings, rather than a
    // live sidechain against the program audio.
    this.entrainmentMaster = this.audioContext.createGain();
    this.entrainmentMaster.gain.value = 1.0;
    // A bus limiter protects against coincident peaks when legacy binaural,
    // isochronic, texture, and the new additive field align.
    this.entrainmentLimiter = this.audioContext.createDynamicsCompressor();
    this.entrainmentLimiter.threshold.value = -16;
    this.entrainmentLimiter.knee.value = 8;
    this.entrainmentLimiter.ratio.value = 10;
    this.entrainmentLimiter.attack.value = 0.004;
    this.entrainmentLimiter.release.value = 0.2;
    // One BPM-locked gain stage sits after every synthesized brainwave
    // source. The existing low-bass pulse LFO drives it, so binaural,
    // texture, isochronic, breath, and polytemporal output all carry the same
    // shallow crest/trough timing instead of behaving as unrelated effects.
    this.entrainmentRippleBus = this.audioContext.createGain();
    this.entrainmentRippleBus.gain.value = 1;
    this.entrainmentMaster.connect(this.entrainmentRippleBus);
    this.entrainmentRippleBus.connect(this.entrainmentLimiter);
    this.entrainmentLimiter.connect(this.audioContext.destination);
    this.rhythmicCue = new AudibleRhythmCueController(this.audioContext, this.entrainmentMaster);

    // Binaural beat synthesis — HYBRID monaural + binaural.
    //
    // A pure binaural beat (tone A only in the left ear, tone B only in the
    // right) depends entirely on the brainstem resolving an interaural phase
    // difference. That is fragile: it collapses with any headphone seating
    // asymmetry, any mono downmix, and — critically — it is inaudible if the
    // carrier sits below the music bed. The Director reported "zero success
    // ever", and the level ceiling below (0.008, i.e. -48 dB) meant the beat
    // was buried ~45 dB under a music bed peaking at 0.68. No entrainment
    // signal, no entrainment.
    //
    // The fix is a crossfeed matrix: each ear receives most of its own tone
    // plus a fraction of the other. At crossfeed = 0 this is a classic
    // binaural beat; at 0.5 both tones reach both ears equally and the result
    // is a MONAURAL beat — the same robust "two strings slightly out of tune"
    // pulse that survives any transducer, any seating, and any downmix. The
    // default sits between the two so the brainstem gets BOTH cues at once.
    this.leftOsc = this.audioContext.createOscillator();
    this.rightOsc = this.audioContext.createOscillator();
    this.merger = this.audioContext.createChannelMerger(2);
    this.binauralGain = this.audioContext.createGain();

    this.leftOsc.type = "sine";
    this.rightOsc.type = "sine";
    this.binauralGain.gain.value = 0.0; // Start muted

    // Crossfeed matrix. direct = 1 - c (own ear), cross = c (opposite ear).
    this.binauralCrossfeed = 0.35;
    this.leftDirect = this.audioContext.createGain();
    this.leftCross = this.audioContext.createGain();
    this.rightDirect = this.audioContext.createGain();
    this.rightCross = this.audioContext.createGain();
    this.leftDirect.gain.value = 1 - this.binauralCrossfeed;
    this.leftCross.gain.value = this.binauralCrossfeed;
    this.rightDirect.gain.value = 1 - this.binauralCrossfeed;
    this.rightCross.gain.value = this.binauralCrossfeed;

    this.leftOsc.connect(this.leftDirect);
    this.leftOsc.connect(this.leftCross);
    this.rightOsc.connect(this.rightDirect);
    this.rightOsc.connect(this.rightCross);
    this.leftDirect.connect(this.merger, 0, 0); // left tone -> left ear
    this.leftCross.connect(this.merger, 0, 1);  // left tone -> right ear
    this.rightDirect.connect(this.merger, 0, 1); // right tone -> right ear
    this.rightCross.connect(this.merger, 0, 0);  // right tone -> left ear
    this.merger.connect(this.binauralGain);
    this.binauralGain.connect(this.entrainmentMaster);

    // Phase sweep: a slow, smooth, INTENTIONAL glide of the actual
    // entrainment rate between adjacent hypnotic bands (alpha bridge <->
    // theta, or theta <-> delta at the deepest categories) instead of
    // holding one Hz dead level. Director, 2026-07-24: "oscillating between
    // hypnotic phases like theta etc... pleasant, hypnotic, slow waves...
    // focus on unconscious subject as result." Real sleep-onset EEG doesn't
    // lock to a single frequency — it drifts through bands — and a fixed
    // target risks habituation (the brain adapts to a steady stimulus and
    // stops following it). A single sine LFO drives every frequency-locked
    // layer (binaural beat, ripple, isochronic rate) together so they glide
    // as one coherent wave, never desynchronized from each other. This is
    // deliberately smooth/periodic — the OPPOSITE character from
    // entropicDrift's irregular jitter (a separate, NSFW-pilot texture) —
    // because "pleasant... slow waves" calls for something rhythmic and
    // oceanic, not unpredictable.
    this.phaseSweepLfo = this.audioContext.createOscillator();
    this.phaseSweepLfo.type = "sine";
    // Director, 2026-07-24: "remember the waves are binaural, so you have to
    // freq shift 1 ear" — leftOsc stays fixed at its carrier frequency; only
    // rightOsc gets the sweep offset. The DIFFERENCE between the two ears is
    // what the brainstem resolves as the beat, so shifting one ear by the
    // full offset sweeps that beat exactly as much as shifting both ears
    // oppositely by half would — simpler, and matches how a binaural
    // generator is normally described (fixed reference ear + shifted ear).
    this.phaseSweepToRight = this.audioContext.createGain();
    this.phaseSweepToRight.gain.value = 0;
    this.phaseSweepLfo.connect(this.phaseSweepToRight);
    this.phaseSweepToRight.connect(this.rightOsc.frequency);
    // Ripple and isochronic LFOs track the beat/target Hz directly (not a
    // stereo difference), so they take the same full offset as the right ear.
    this.phaseSweepToRipple = this.audioContext.createGain();
    this.phaseSweepToIso = this.audioContext.createGain();
    this.phaseSweepToRipple.gain.value = 0;
    this.phaseSweepToIso.gain.value = 0;
    this.phaseSweepLfo.connect(this.phaseSweepToRipple);
    this.phaseSweepLfo.connect(this.phaseSweepToIso);
    // phaseSweepToRipple/Iso feed rippleLfo/isoLfo below, once those
    // oscillators exist.

    // Channel-owned ambient texture. This is synthesized locally so every
    // channel has a dependable low-level bed without borrowing mismatched files.
    const noiseBuffer = this.audioContext.createBuffer(1, this.audioContext.sampleRate * 2, this.audioContext.sampleRate);
    const noiseData = noiseBuffer.getChannelData(0);
    for (let index = 0; index < noiseData.length; index += 1) {
      noiseData[index] = Math.random() * 2 - 1;
    }
    this.textureSource = this.audioContext.createBufferSource();
    this.textureSource.buffer = noiseBuffer;
    this.textureSource.loop = true;
    this.textureFilter = this.audioContext.createBiquadFilter();
    this.textureFilter.type = "lowpass";
    this.textureGain = this.audioContext.createGain();
    this.textureGain.gain.value = 0;
    this.textureSource.connect(this.textureFilter);
    this.textureFilter.connect(this.textureGain);
    this.textureGain.connect(this.entrainmentMaster);

    // Frequency-locked brainwave ripple. This is control coupling only: no
    // subliminal program audio enters the audible effects graph. A shallow LFO
    // makes the texture breathe at the same target used by the subliminal and
    // binaural engines, so the layers feel like one field.
    this.rippleLfo = this.audioContext.createOscillator();
    this.rippleLfo.type = "sine";
    this.rippleDepth = this.audioContext.createGain();
    this.rippleDepth.gain.value = 0;
    this.rippleLfo.connect(this.rippleDepth);
    this.rippleDepth.connect(this.textureGain.gain);
    this.phaseSweepToRipple.connect(this.rippleLfo.frequency);

    // Slow breath envelope, separate from the target-Hz ripple. A phase-shifted
    // cosine starts low, rises across the first half-cycle (inhale), then falls
    // across the second half (exhale). The default 6 s cycle is 3 s in / 3 s out.
    this.breathWaveLfo = this.audioContext.createOscillator();
    const breathReal = new Float32Array([0, -1]);
    const breathImag = new Float32Array([0, 0]);
    this.breathWaveLfo.setPeriodicWave(
      this.audioContext.createPeriodicWave(breathReal, breathImag),
    );
    this.breathTextureDepth = this.audioContext.createGain();
    this.breathFieldDepth = this.audioContext.createGain();
    this.breathTextureDepth.gain.value = 0;
    this.breathFieldDepth.gain.value = 0;
    this.breathWaveLfo.connect(this.breathTextureDepth);
    this.breathWaveLfo.connect(this.breathFieldDepth);
    this.breathTextureDepth.connect(this.textureGain.gain);
    this.breathFieldDepth.connect(this.binauralGain.gain);

    // A restrained low pulse follows channel BPM. It remains beneath the music
    // and guidance; it is an effect layer, never the main program. Carrier
    // sits at 42 Hz rather than a more "musical" low note — closer to the
    // felt-bass threshold than a heard pitch.
    this.pulseCarrier = this.audioContext.createOscillator();
    this.pulseCarrier.type = "sine";
    this.pulseCarrier.frequency.value = 42;
    this.pulseGain = this.audioContext.createGain();
    this.pulseGain.gain.value = 0;
    this.pulseLfo = this.audioContext.createOscillator();
    this.pulseDepth = this.audioContext.createGain();
    this.pulseDepth.gain.value = 0;
    this.pulseLfo.connect(this.pulseDepth);
    this.pulseDepth.connect(this.pulseGain.gain);
    this.sharedBassRippleDepth = this.audioContext.createGain();
    this.sharedBassRippleDepth.gain.value = 0;
    this.pulseLfo.connect(this.sharedBassRippleDepth);
    this.sharedBassRippleDepth.connect(this.entrainmentRippleBus.gain);
    this.pulseCarrier.connect(this.pulseGain);
    this.pulseGain.connect(this.entrainmentMaster);
    this.breathFieldDepth.connect(this.pulseGain.gain);

    // Isochronic layer: FELT, not heard. A pure sine tone amplitude-gated at
    // a slow rate is a discrete, locatable pitch that clicks on and off —
    // exactly the "loud train noise" character to avoid. Filtered low-passed
    // noise carries no pitch to notice; gated the same way it reads as a
    // pressure/rumble that swells and fades rather than a tone that chugs.
    // Brown-ish noise (integrated white noise) concentrates energy in the
    // low end before filtering even touches it, so the filter isn't doing
    // all the work of hiding hiss. Off by default (isoGain stays at 0)
    // until applyBrainwaveTarget/applyAudioScene turns it on.
    const isoNoiseBuffer = this.audioContext.createBuffer(
      1, this.audioContext.sampleRate * 2, this.audioContext.sampleRate,
    );
    const isoNoiseData = isoNoiseBuffer.getChannelData(0);
    let isoIntegrator = 0;
    for (let index = 0; index < isoNoiseData.length; index += 1) {
      const white = Math.random() * 2 - 1;
      isoIntegrator = (isoIntegrator + 0.02 * white) / 1.02;
      isoNoiseData[index] = isoIntegrator * 3.5;
    }
    this.isoSource = this.audioContext.createBufferSource();
    this.isoSource.buffer = isoNoiseBuffer;
    this.isoSource.loop = true;
    this.isoFilter = this.audioContext.createBiquadFilter();
    this.isoFilter.type = "lowpass";
    this.isoFilter.frequency.value = 55; // felt, not a "note" you could hum back
    this.isoFilter.Q.value = 0.7;
    this.isoGain = this.audioContext.createGain();
    this.isoGain.gain.value = 0;
    this.isoLfo = this.audioContext.createOscillator();
    this.isoLfo.type = "sine";
    this.isoDepth = this.audioContext.createGain();
    this.isoDepth.gain.value = 0;
    this.isoLfo.connect(this.isoDepth);
    this.isoDepth.connect(this.isoGain.gain);
    this.isoSource.connect(this.isoFilter);
    this.isoFilter.connect(this.isoGain);
    this.isoGain.connect(this.entrainmentMaster);
    this.phaseSweepToIso.connect(this.isoLfo.frequency);

    this.leftOsc.start();
    this.rightOsc.start();
    this.textureSource.start();
    this.rippleLfo.start();
    this.breathWaveLfo.start();
    this.pulseCarrier.start();
    this.pulseLfo.start();
    this.isoSource.start();
    this.isoLfo.start();
    this.phaseSweepLfo.start();
    // Additive and independently gated: failure to load AudioWorklet leaves
    // every established binaural/subliminal/isochronic path untouched.
    this._setupPolytemporalField();
    this._setupHypnoticSoundscape();
    this.applyAudioScene(this.currentAudioScene || {});
  }

  async _setupPolytemporalField() {
    if (!this.audioContext || this.polytemporalField) return;
    const field = new PolytemporalAuditoryField(this.audioContext, this.entrainmentMaster);
    this.polytemporalField = field;
    await field.start({
      ...polytemporalSceneSettings(this.currentAudioScene || {}),
      clockEpoch: this.entrainmentEpoch,
      enabled: Boolean(this.entrainmentActive)
        && polytemporalSceneSettings(this.currentAudioScene || {}).enabled,
    });
  }

  async _setupHypnoticSoundscape() {
    if (!this.audioContext || this.hypnoticSoundscape) return;
    const soundscape = new HypnoticSoundscape(this.audioContext, this.entrainmentMaster);
    this.hypnoticSoundscape = soundscape;
    await soundscape.start({
      ...somaticSceneSettings(this.currentAudioScene || {}),
      clockEpoch: this.entrainmentEpoch,
      enabled: Boolean(this.entrainmentActive)
        && somaticSceneSettings(this.currentAudioScene || {}).enabled,
    });
    // The legacy sine pulse remains a fallback for browsers without
    // AudioWorklet. Once the shaped lub-dub is available, mute the older
    // continuous throb so the two heart rhythms cannot stack.
    if (soundscape.node && this.pulseGain && this.audioContext) {
      this.pulseGain.gain.setTargetAtTime(0, this.audioContext.currentTime, 1.5);
      this.pulseDepth?.gain.setTargetAtTime(0, this.audioContext.currentTime, 1.5);
    }
  }

  applyAudioScene(scene = {}) {
    this.currentAudioScene = scene && typeof scene === "object" ? scene : {};
    if (!this.audioContext) return;
    const now = this.audioContext.currentTime;
    this.rhythmicCue?.apply(this.currentAudioScene);
    this.rhythmicCue?.setPlaying(Boolean(this.entrainmentActive && !this.audio?.paused));

    // Director, 2026-07-24: "brainwaves are way too loud, they should be
    // masked by music" — the whole synthesized bus ducks together based on
    // the scene's own authored musicDucking intent (set per-category in
    // pipeline.py's _build_audio_scene, previously generated but never
    // actually consumed anywhere in the frontend). "strong" (deepest
    // categories, sparsest music) hides the effects bus the most; "light"
    // (denser sung mixes) allows the most headroom.
    const duckMode = String(this.currentAudioScene.musicDucking || "moderate").toLowerCase();
    const duckMultiplier = { light: 0.82, moderate: 0.68, strong: 0.52 }[duckMode] ?? 0.68;
    this.entrainmentMaster?.gain.setTargetAtTime(duckMultiplier, now, 2.5);

    const texture = String(this.currentAudioScene.textureMode || "").toLowerCase();
    const textureProfiles = {
      ocean: [520, 0.007],
      rain: [1300, 0.006],
      mist: [760, 0.004],
      shimmer: [2200, 0.003],
    };
    const [cutoff, textureLevel] = textureProfiles[texture] || [680, texture ? 0.0035 : 0];
    this.textureFilter?.frequency.setTargetAtTime(cutoff, now, 1.5);
    this.textureGain?.gain.setTargetAtTime(textureLevel, now, 2.5);

    const beatMode = String(this.currentAudioScene.beatMode || "").toLowerCase();
    const bpm = resolveSceneBpm(this.currentAudioScene.bpm, 36);
    this.pulseLfo?.frequency.setTargetAtTime(bpm / 60, now, 1);
    const configuredSharedRipple = Number(this.currentAudioScene.bassBrainwaveRippleDepth);
    const sharedRippleDepth = this.currentAudioScene.bassBrainwaveRippleEnabled === false
      ? 0
      : Number.isFinite(configuredSharedRipple)
        ? Math.max(0, Math.min(0.30, configuredSharedRipple))
        : 0.12;
    // The AudioParam base plus bipolar LFO yields [1-depth, 1], so the shared
    // ripple never boosts beyond the established limiter/mix ceiling.
    this.entrainmentRippleBus?.gain.setTargetAtTime(1 - sharedRippleDepth / 2, now, 1);
    this.sharedBassRippleDepth?.gain.setTargetAtTime(sharedRippleDepth / 2, now, 1);
    this.currentBassRippleHz = bpm / 60;
    this.currentBassRippleDepth = sharedRippleDepth;
    const pulseOn = beatMode && beatMode !== "off" && beatMode !== "none";
    // Slower time constant (3s vs the old 2s) so the pulse breathes in and
    // out rather than snapping to level — a fast ramp on a low tone is part
    // of what reads as a "chugging train" rather than a felt heartbeat.
    // Levels cut again (0.002->0.0014, 0.0012->0.0008) on top of the new
    // master duck — "way too loud" per Director.
    const useLegacyPulse = !this.hypnoticSoundscape?.node;
    this.pulseGain?.gain.setTargetAtTime(useLegacyPulse && pulseOn ? 0.0014 : 0, now, 3);
    this.pulseDepth?.gain.setTargetAtTime(useLegacyPulse && pulseOn ? 0.0008 : 0, now, 3);

    // Legacy sidecars used 1.5 s per phase, which reads as a quick volume
    // wobble rather than a long breath. Treat those old values as unset and
    // move the complete field to the new 3.5-in / 4-out somatic clock.
    const authoredInhale = Number(this.currentAudioScene.breathInSeconds);
    const authoredExhale = Number(this.currentAudioScene.breathOutSeconds);
    const inhaleSeconds = Math.max(2.5, Math.min(5, authoredInhale >= 2.5 ? authoredInhale : 3.5));
    const exhaleSeconds = Math.max(2.5, Math.min(6, authoredExhale >= 2.5 ? authoredExhale : 4));
    const breathCycleSeconds = inhaleSeconds + exhaleSeconds;
    const breathWaveOn = this.currentAudioScene.breathWaveEnabled !== false;
    const configuredBreathDepth = Number(this.currentAudioScene.breathWaveDepth);
    const breathWaveDepth = Number.isFinite(configuredBreathDepth)
      ? Math.max(0, Math.min(0.004, configuredBreathDepth))
      : 0.002;
    this.currentBreathCycleSeconds = breathCycleSeconds;
    this.currentBreathWaveDepth = breathWaveOn ? breathWaveDepth : 0;
    this.breathWaveLfo?.frequency.setTargetAtTime(1 / breathCycleSeconds, now, 1);
    this.breathTextureDepth?.gain.setTargetAtTime(breathWaveOn ? Math.min(0.0015, breathWaveDepth) : 0, now, 1.5);
    this.breathFieldDepth?.gain.setTargetAtTime(
      breathWaveOn ? Math.min(0.001, breathWaveDepth) : 0,
      now,
      1.5,
    );
    this.applyIsochronicScene();
    const polytemporalSettings = polytemporalSceneSettings(this.currentAudioScene);
    this.polytemporalField?.apply({
      ...polytemporalSettings,
      enabled: Boolean(this.entrainmentActive) && polytemporalSettings.enabled,
    });
    const somaticSettings = somaticSceneSettings(this.currentAudioScene);
    this.hypnoticSoundscape?.apply({
      ...somaticSettings,
      clockEpoch: this.entrainmentEpoch,
      enabled: Boolean(this.entrainmentActive) && somaticSettings.enabled,
    });
    this._scheduleEntropicDrift();
  }

  setAudibleRhythmEnabled(enabled) {
    this.audibleRhythmOverride = Boolean(enabled);
    const baseScene = this.currentAudioScene && typeof this.currentAudioScene === "object"
      ? this.currentAudioScene
      : {};
    const canEnable = baseScene.audibleRhythmEnabled === true;
    const nextEnabled = this.audibleRhythmOverride && canEnable;
    this.currentAudioScene = {
      ...baseScene,
      audibleRhythmEnabled: nextEnabled,
      audibleRhythmMode: nextEnabled ? (baseScene.audibleRhythmMode || "opening_then_rare") : "off",
    };
    this.applyAudioScene(this.currentAudioScene);
    return nextEnabled;
  }

  /**
   * Entropic drift (pilot, NSFW-only by default — see pipeline.py
   * _build_audio_scene). Director, 2026-07-24: asked whether brainwave audio
   * could be tuned toward psychedelic EEG signatures. The honest answer:
   * not directly — binaural/isochronic entrainment is a SYNCHRONIZATION
   * technology (it pulls the brain toward one steady target Hz), while the
   * psilocybin/LSD literature shows alpha suppression and rising signal
   * entropy, which is structurally the opposite mechanism. What IS a fair
   * translation: instead of holding the isochronic and pulse layers locked
   * to a fixed rate/depth, let them wander — irregular timer, bounded random
   * offset from the scene's base target each tick (not a compounding random
   * walk, so it can never drift far from the intended brainwave target).
   * This reads as a dissolving/unanchored texture rather than a locked
   * rhythm, without touching the primary binaural descent that does the
   * actual theta/delta work.
   */
  _scheduleEntropicDrift() {
    if (this._entropicDriftTimer) {
      clearTimeout(this._entropicDriftTimer);
      this._entropicDriftTimer = null;
    }
    if (!this.audioContext) return;
    if (!Boolean(this.currentAudioScene?.entropicDriftEnabled)) return;

    const now = this.audioContext.currentTime;
    const depth = Math.max(0, Math.min(1, Number(this.currentAudioScene?.entropicDriftDepth ?? 0.3)));

    const baseHz = Number(this.currentBrainwaveHz) || 0;
    if (baseHz > 0 && this.isoLfo) {
      const hzJitter = baseHz * (1 + (Math.random() * 2 - 1) * depth * 0.5);
      this.isoLfo.frequency.setTargetAtTime(Math.max(0.5, hzJitter), now, 2.5);
    }

    const baseLevel = Number(this.currentAudioScene?.isochronicLevel) || 0.005;
    if (this.isoGain) {
      const levelJitter = baseLevel * (1 + (Math.random() * 2 - 1) * depth);
      const clampedLevel = Math.max(0, Math.min(0.016, levelJitter));
      this.isoGain.gain.setTargetAtTime(clampedLevel, now, 3);
      this.isoDepth?.gain.setTargetAtTime(clampedLevel, now, 3);
    }

    const baseBpm = resolveSceneBpm(this.currentAudioScene?.bpm, 36);
    if (this.pulseLfo) {
      const bpmJitter = baseBpm * (1 + (Math.random() * 2 - 1) * depth * 0.4);
      this.pulseLfo.frequency.setTargetAtTime(Math.max(0.2, bpmJitter / 60), now, 2.5);
    }

    const rateSeconds = Math.max(3, Math.min(20, Number(this.currentAudioScene?.entropicDriftRateSeconds) || 7));
    const nextDelayMs = (rateSeconds + (Math.random() * 2 - 1) * rateSeconds * 0.4) * 1000;
    this._entropicDriftTimer = setTimeout(() => this._scheduleEntropicDrift(), nextDelayMs);
  }

  _stopEntropicDrift() {
    if (this._entropicDriftTimer) {
      clearTimeout(this._entropicDriftTimer);
      this._entropicDriftTimer = null;
    }
  }

  /**
   * Isochronic tone: opt-in via scene.isochronicMode ("off" | "subtle" |
   * "present"). Unlike binaural/ripple, this works identically on speakers
   * and headphones, so it is a real fallback for listeners not wearing
   * headphones — and a genuine second entrainment channel for those who are.
   * Depth is deliberately deeper than the BPM sub-pulse (a true on/off gate
   * reads as entrainment; a barely-there wobble does not), while the tone
   * level itself stays low and masked, matching this file's existing
   * restraint on every other synthesized layer.
   */
  applyIsochronicScene() {
    if (!this.audioContext || !this.isoGain) return;
    const now = this.audioContext.currentTime;
    const mode = String(this.currentAudioScene?.isochronicMode || "off").toLowerCase();
    const on = this.currentBrainwaveHz > 0 && mode !== "off" && mode !== "none";
    const configuredLevel = Number(this.currentAudioScene?.isochronicLevel);
    // Lower ceiling than the original tone-based design: filtered noise reads
    // as felt pressure rather than a heard pitch at the same level, but the
    // goal is still "felt, not audible" — trim further rather than rely on
    // the timbre change alone. Ceiling cut again 0.016->0.010 (plus the new
    // entrainmentMaster duck on top) per Director: "way too loud... should
    // be masked by music."
    const level = Number.isFinite(configuredLevel)
      ? Math.max(0, Math.min(0.010, configuredLevel))
      : (mode === "present" ? 0.007 : 0.0035);
    // Gate depth as a fraction of level: base==depth means the trough is
    // silence (a real gate) rather than a wobble around a nonzero floor.
    // 2.5s time constant (up from 1.5s) so the swell is slow enough to feel
    // like a tide, not a switch.
    this.isoGain.gain.setTargetAtTime(on ? level : 0, now, 2.5);
    this.isoDepth?.gain.setTargetAtTime(on ? level : 0, now, 2.5);
  }

  layerStatus() {
    const rhythmStatus = this.rhythmicCue?.status?.() || {
      active: false,
      enabled: false,
      phase: "idle",
      tickIndex: 0,
      bpm: 0,
    };
    return {
      music: Boolean(this.audio?.src || this.audio?.currentSrc),
      binaural: Boolean(
        this.currentBrainwaveHz > 0
        && this.currentAudioScene?.binauralEnabled !== false
        && this.binauralGain,
      ),
      texture: Boolean(this.currentAudioScene?.textureMode && this.textureGain),
      pulse: Boolean(this.currentAudioScene?.beatMode && this.pulseGain),
      ripple: Boolean(this.currentBrainwaveHz > 0 && this.rippleDepth),
      isochronic: Boolean(
        this.currentBrainwaveHz > 0
        && this.isoGain
        && !["off", "none", ""].includes(String(this.currentAudioScene?.isochronicMode || "off").toLowerCase())
      ),
      brainwave_target_hz: Number(this.currentBrainwaveHz || 0),
      ripple_hz: Number(this.currentRippleHz || 0),
      ripple_depth: Number(this.currentRippleDepth || 0),
      breath_wave: Boolean(this.currentBreathWaveDepth > 0 && this.breathWaveLfo),
      breath_cycle_seconds: Number(this.currentBreathCycleSeconds || 0),
      breath_wave_depth: Number(this.currentBreathWaveDepth || 0),
      entropic_drift: Boolean(this.currentAudioScene?.entropicDriftEnabled && this._entropicDriftTimer),
      phase_sweep: Boolean(
        resolvePhaseSweepDepth(this.currentBrainwaveHz, this.currentAudioScene) > 0
        && this.phaseSweepLfo,
      ),
      phase_sweep_depth_hz: resolvePhaseSweepDepth(this.currentBrainwaveHz, this.currentAudioScene),
      polytemporal: Boolean(this.polytemporalField?.settings?.enabled && this.polytemporalField?.node),
      polytemporal_stage: this.polytemporalField?.node ? "automatic_trajectory" : "unavailable",
      polytemporal_stimulus: describePolytemporalStimulus(
        this.polytemporalField?.settings || polytemporalSceneSettings(this.currentAudioScene || {}),
      ),
      shared_bass_ripple: Boolean(this.currentBassRippleDepth > 0 && this.entrainmentRippleBus),
      shared_bass_ripple_hz: Number(this.currentBassRippleHz || 0),
      shared_bass_ripple_depth: Number(this.currentBassRippleDepth || 0),
      heartbeat_audio: Boolean(
        this.hypnoticSoundscape?.node
        && this.hypnoticSoundscape?.settings?.enabled
        && this.hypnoticSoundscape?.settings?.heartbeatEnabled
      ),
      heartbeat_bpm: Number(this.hypnoticSoundscape?.settings?.heartbeatBpm || 0),
      breath_sound: Boolean(
        this.hypnoticSoundscape?.node
        && this.hypnoticSoundscape?.settings?.enabled
        && this.hypnoticSoundscape?.settings?.breathSoundEnabled
      ),
      audible_rhythm: rhythmStatus.active,
      audible_rhythm_active: rhythmStatus.active,
      audible_rhythm_enabled: rhythmStatus.enabled,
      audible_rhythm_phase: rhythmStatus.phase,
      audible_rhythm_tick_index: rhythmStatus.tickIndex,
      audible_rhythm_bpm: rhythmStatus.bpm,
      condition_locked: this.currentAudioScene?.condition_lock === true,
      condition_schema: String(this.currentAudioScene?.condition_schema || ""),
      condition_source: String(this.currentAudioScene?.condition_source || ""),
      condition_role: String(this.currentAudioScene?.condition_role || ""),
    };
  }

  applyBrainwaveTarget(hz) {
    if (!this.audioContext) this.setupEntrainment();
    if (!this.audioContext) return;
    if (this.audioContext.state === "suspended" && !this.transportPaused) {
      this.audioContext.resume();
    }

    const targetHz = Number(hz) || 0;
    this.currentBrainwaveHz = targetHz;
    if (targetHz > 0) {
      // Carrier for the audible binaural beat. Lower than a typical 200 Hz
      // generator so it sits closer to felt than heard, but still high enough
      // for the brainstem to resolve a stable interaural beat (binaural
      // perception needs SOME audible carrier). The operator's "Base carrier"
      // slider now reaches this; 128 Hz is the fallback when no lab scene is
      // active.
      const configuredBase = Number(this.currentAudioScene?.beatCarrierBaseHz);
      const baseFreq = Number.isFinite(configuredBase) && configuredBase >= 40
        ? Math.min(1200, configuredBase)
        : 128;
      this.leftOsc.frequency.setTargetAtTime(baseFreq - targetHz / 2, this.audioContext.currentTime, 0.5);
      this.rightOsc.frequency.setTargetAtTime(baseFreq + targetHz / 2, this.audioContext.currentTime, 0.5);
      this.rippleLfo.frequency.setTargetAtTime(targetHz, this.audioContext.currentTime, 0.5);
      this.isoLfo?.frequency.setTargetAtTime(targetHz, this.audioContext.currentTime, 0.5);
      const configuredRipple = Number(this.currentAudioScene?.brainwaveRippleDepth);
      const rippleDepth = Number.isFinite(configuredRipple)
        ? Math.max(0, Math.min(0.003, configuredRipple))
        : 0.001;
      this.currentRippleHz = targetHz;
      this.currentRippleDepth = rippleDepth;
      this.rippleDepth.gain.setTargetAtTime(rippleDepth, this.audioContext.currentTime, 1.5);
      
      // Keep the standalone carrier masked under the actual music, but NOT
      // buried beneath it. History: this ceiling was trimmed 0.015 -> 0.012 ->
      // 0.008 (default 0.008 -> 0.006 -> 0.004) chasing "less audible, more
      // felt". That went too far: 0.004 is -48 dB, roughly 45 dB below a music
      // bed peaking at 0.68. A binaural beat the listener cannot hear cannot
      // entrain — the brainstem has no interaural phase difference to resolve.
      // The Director's report was "zero success ever".
      //
      // Restored to an audible-but-subordinate band. 0.05 is ~-26 dB: clearly
      // present under the bed, still far below the voice, and no longer
      // competing with the program. The hybrid crossfeed above means the beat
      // also survives as a monaural pulse, so this level is a floor for
      // entrainment rather than a fragile stereo-only cue.
      const configuredLevel = Number(this.currentAudioScene?.binauralLevel);
      const binauralLevel = Number.isFinite(configuredLevel)
        ? Math.max(0, Math.min(0.12, configuredLevel))
        : 0.05;
      const binauralEnabled = this.currentAudioScene?.binauralEnabled !== false;
      this.binauralGain.gain.setTargetAtTime(binauralEnabled ? binauralLevel : 0, this.audioContext.currentTime, 3.0);
      this.applyIsochronicScene();

      // Director, 2026-07-24: "oscillating between hypnotic phases like
      // theta etc... pleasant, hypnotic, slow waves... focus on unconscious
      // subject as result." Depth scales with the category's own target so
      // the deepest, most fragile categories (already down near delta) get
      // a proportionally gentler glide and never swing back up toward
      // alertness — capped in absolute Hz too so even the widest opening-
      // category sweep stays inside a believable hypnotic range.
      const sweepDepthHz = resolvePhaseSweepDepth(targetHz, this.currentAudioScene);
      // ~3.5 minutes per full glide — slow enough to feel oceanic and
      // pleasant rather than a perceptible wobble.
      const sweepPeriodSeconds = 210;
      this.phaseSweepLfo?.frequency.setTargetAtTime(1 / sweepPeriodSeconds, this.audioContext.currentTime, 4);
      this.phaseSweepToRight?.gain.setTargetAtTime(sweepDepthHz, this.audioContext.currentTime, 4);
      this.phaseSweepToRipple?.gain.setTargetAtTime(sweepDepthHz, this.audioContext.currentTime, 4);
      this.phaseSweepToIso?.gain.setTargetAtTime(sweepDepthHz, this.audioContext.currentTime, 4);
      this.polytemporalField?.apply({ targetHz });
    } else {
      this.currentRippleHz = 0;
      this.currentRippleDepth = 0;
      this.binauralGain.gain.setTargetAtTime(0.0, this.audioContext.currentTime, 1.0);
      this.rippleDepth.gain.setTargetAtTime(0.0, this.audioContext.currentTime, 1.0);
      this.isoGain?.gain.setTargetAtTime(0.0, this.audioContext.currentTime, 1.0);
      this.isoDepth?.gain.setTargetAtTime(0.0, this.audioContext.currentTime, 1.0);
      this.phaseSweepToRight?.gain.setTargetAtTime(0.0, this.audioContext.currentTime, 1.0);
      this.phaseSweepToRipple?.gain.setTargetAtTime(0.0, this.audioContext.currentTime, 1.0);
      this.phaseSweepToIso?.gain.setTargetAtTime(0.0, this.audioContext.currentTime, 1.0);
    }
  }

  stopEntrainment() {
    this.entrainmentActive = false;
    this._stopEntropicDrift();
    if (this.binauralGain && this.audioContext) {
      this.binauralGain.gain.setTargetAtTime(0.0, this.audioContext.currentTime, 0.5);
      this.rippleDepth?.gain.setTargetAtTime(0.0, this.audioContext.currentTime, 0.5);
      this.breathTextureDepth?.gain.setTargetAtTime(0.0, this.audioContext.currentTime, 0.5);
      this.breathFieldDepth?.gain.setTargetAtTime(0.0, this.audioContext.currentTime, 0.5);
      this.isoGain?.gain.setTargetAtTime(0.0, this.audioContext.currentTime, 0.5);
      this.isoDepth?.gain.setTargetAtTime(0.0, this.audioContext.currentTime, 0.5);
      this.phaseSweepToRight?.gain.setTargetAtTime(0.0, this.audioContext.currentTime, 0.5);
      this.phaseSweepToRipple?.gain.setTargetAtTime(0.0, this.audioContext.currentTime, 0.5);
      this.phaseSweepToIso?.gain.setTargetAtTime(0.0, this.audioContext.currentTime, 0.5);
    }
    this.polytemporalField?.stop();
    this.hypnoticSoundscape?.stop();
  }

  setNarrationWindow(seconds = 0) {
    this.polytemporalField?.setNarrationWindow(seconds);
    this.hypnoticSoundscape?.setNarrationWindow(seconds, 0.32);
  }

  loadTrack(track) {
    if (!track?.audio_src) {
      throw new Error("Cannot load a track without audio_src");
    }
    const trackScene = track.audio_scene || {};
    this.currentAudioScene = this.audibleRhythmOverride === false
      ? { ...trackScene, audibleRhythmEnabled: false, audibleRhythmMode: "off" }
      : trackScene;
    if (this.rhythmicCue) {
      const sessionKey = track.session_id || track.sessionId || this.currentAudioScene.session_id || "default";
      if (!this.rhythmicCue.sessionKey) this.rhythmicCue.startSession(sessionKey);
      else if (sessionKey !== this.rhythmicCue.sessionKey) this.rhythmicCue.startSession(sessionKey);
    }
    this.currentBrainwaveHz = Number(
      track.brainwave_target_hz
      || this.currentAudioScene.targetBrainwaveHz
      || this.currentAudioScene.binHz
      || 0
    );
    // Theme changes may temporarily loop a silent gesture-preservation source.
    // A real main-deck track must always clear that bootstrap state.
    this.audio.loop = false;
    this.audio.src = track.audio_src;
    this.audio.load();
    if (this.audioContext) this.applyAudioScene(this.currentAudioScene);
  }

  async play() {
    this.entrainmentActive = true;
    this.transportPaused = false;
    // Invoke media playback before awaiting context work so the browser's
    // transient user-activation token is consumed by the primary deck first.
    const primaryPlayback = this.audio.play();
    const contextResume = this.audioContext?.state === "suspended"
      ? this.audioContext.resume()
      : Promise.resolve();
    try {
      await Promise.all([primaryPlayback, contextResume]);
    } catch (error) {
      this.entrainmentActive = false;
      throw error;
    }
    // The HTML media element is the required transport. Browser WebAudio
    // capability failures must not turn a successful native play() into a
    // rejected Play action (Safari/private mode and embedded webviews often
    // expose only part of the WebAudio API).
    try {
      if (this.currentBrainwaveHz > 0) {
        this.applyBrainwaveTarget(this.currentBrainwaveHz);
      }
      this.applyAudioScene(this.currentAudioScene || {});
    } catch (error) {
      console.warn("[AceLabReact] optional entrainment setup failed", error);
    }
    // The native deck is the transport. Optional worklets and synthesized
    // texture must never hold the Play promise open: a slow AudioWorklet
    // registration used to make the button appear dead even though the media
    // element had already accepted play(). Finish those layers in the
    // background and keep their failure non-fatal.
    this._finishOptionalPlaybackSetup();
  }

  _finishOptionalPlaybackSetup() {
    Promise.resolve()
      .then(async () => {
        if (!this.entrainmentActive) return;
        if (!this.polytemporalField) await this._setupPolytemporalField();
        this.polytemporalField?.apply(polytemporalSceneSettings(this.currentAudioScene || {}));
        if (!this.entrainmentActive) return;
        if (!this.hypnoticSoundscape) await this._setupHypnoticSoundscape();
        const somaticSettings = somaticSceneSettings(this.currentAudioScene || {});
        this.hypnoticSoundscape?.apply({ ...somaticSettings, enabled: somaticSettings.enabled });
      })
      .catch((error) => {
        // Playback is already live; optional ambience can be retried on the
        // next Play without turning a browser capability gap into a transport
        // failure.
        console.warn("[AceLabReact] optional playback layer failed", error);
      });
  }

  pause() {
    this.transportPaused = true;
    this.audio.pause();
    // Suspending freezes oscillator phase, AudioParam automation and the
    // polytemporal worklet at the same instant. Muting/rebuilding each layer
    // independently caused their pulse phases to drift after resume.
    this._stopEntropicDrift();
    return this.audioContext?.state === "running"
      ? this.audioContext.suspend().catch(() => null)
      : Promise.resolve();
  }

  clear() {
    this.audio.removeAttribute("src");
    this.audio.load();
    this.stopEntrainment();
    this.rhythmicCue?.resetSession();
    if (this.rhythmicCue) this.rhythmicCue.sessionKey = "";
    this.updateMediaSessionMetadata(null);
  }

  isPlaying() {
    return !this.audio.paused;
  }

  currentTrackFile() {
    try {
      const url = new URL(this.audio.currentSrc || this.audio.src || "", window.location.href);
      return decodeURIComponent(url.pathname.split("/").pop() || "");
    } catch {
      return "";
    }
  }

  onTrackEnded(listener) {
    this.trackEndedListeners.push(listener);
  }

  onPlaybackStateChanged(listener) {
    this.playbackListeners.push(listener);
  }

  onTrackError(listener) {
    this.trackErrorListeners.push(listener);
  }

  emitPlaybackState(isPlaying) {
    this.updatePlaybackState(isPlaying ? "playing" : "paused");
    this.playbackListeners.forEach((listener) => listener(isPlaying));
  }

  registerMediaSessionHandlers() {
    if (typeof navigator === "undefined" || !navigator.mediaSession) {
      return;
    }

    navigator.mediaSession.setActionHandler("play", () => {
      const action = this.transportHandlers?.play || (() => this.play());
      Promise.resolve(action()).catch(() => {});
    });
    navigator.mediaSession.setActionHandler("pause", () => {
      const action = this.transportHandlers?.pause || (() => this.pause());
      Promise.resolve(action()).catch(() => {});
    });
  }

  setTransportHandlers(handlers = null) {
    this.transportHandlers = handlers && typeof handlers === "object" ? handlers : null;
  }

  updateMediaSessionMetadata(track) {
    if (typeof navigator === "undefined" || !navigator.mediaSession) {
      return;
    }

    if (!track) {
      navigator.mediaSession.metadata = null;
      this.updatePlaybackState("none");
      return;
    }

    if (typeof window !== "undefined" && "MediaMetadata" in window) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: track.title || "HypnoMates",
        artist: track.persona_name || "AceLab",
        album: track.theme_slug || track.category || "Listening Room",
      });
    }
    this.updatePlaybackState(this.isPlaying() ? "playing" : "paused");
  }

  updatePlaybackState(state) {
    if (typeof navigator === "undefined" || !navigator.mediaSession) {
      return;
    }

    try {
      navigator.mediaSession.playbackState = state;
    } catch {
      // Some browsers expose Media Session partially; ignore unsupported states.
    }
  }
}
